import { poweredPlateIds } from '../model/power';
import { selfOpeningCells } from '../model/self-opening';
import { traceDistance, traceRoutes } from '../model/trace-path';
import { SHAPE_TEMPLATES, boundingBox, occupiedCells } from '../model/shape';
import { exitingGate, exitsThroughGate, findExitedFree, occupiedKeysExcept, slideAndExitAgainst, } from '../model/free-move';
import { clearableFree, solveFree } from '../solver/free-solver';
import { shortSolve } from '../solver/short-solve';
import { SOLUTION_TABLE } from './solution-table';
import { DEFAULT_GENERATION_CONFIG, bandConfigFor } from '../config/generation-config';
import { mulberry32 } from './rng';
import { measureBoard, proxyScoreBoard, scoreDifficulty } from './difficulty-score';
import { frameworkBandForLevel, frameworkBandRamp, targetScoreForLevel } from './difficulty-curve';
import { RecipeChain, recipeForLevel } from './level-recipe';
// The density ramp + shape/colour recipe live in `level-recipe.ts` (WP-35.11);
// both are re-exported here so the package's public surface is unchanged.
export { blockCountFor, unlockedShapes, recipeFor, recipeForLevel, RecipeChain, } from './level-recipe';
/**
 * Module 35 (WP-35.8) — the free-slide level generator.
 *
 * Lane/placement-based construction, NOT reverse-construction (the module rule):
 * gates are chosen per colour, shapes are placed into free cells, arrow locks are
 * stamped on, and the candidate board is then **verified with the free solver**
 * before it is returned. Anything unsolvable, trivially solvable, or already
 * touching a gate is discarded and the next attempt runs — so every board this
 * returns is provably clearable under the WP-35.5 movement rules.
 *
 * Deterministic: one seed per level number, so level N is the same board on every
 * device and every run. Attempts continue the same stream, so a retry changes the
 * board without breaking determinism.
 *
 * This is a NEW module; `level-generator.ts` (single-axis, reverse-constructed)
 * stays untouched and remains the live game's generator until the plugin swaps
 * over. Difficulty scoring (WP-35.9), the band/level curve (WP-35.10) and the
 * density ramp + anti-repetition (WP-35.11) build on top of this.
 */
const SEED_SALT = 0x9e3779b9;
const SEED_OFFSET = 1514613532;
/** Stride between seed variants — see `FreeGenerateOptions.seed` (WP-35.12). */
const SEED_VARIANT_STRIDE = 1663821211;
/** Placement tries per block before an attempt is abandoned. */
const PLACEMENT_TRIES = 40;
/** Node budget for the depth-limited "is this trivially easy?" screen. */
const TOO_EASY_NODE_BUDGET = 2000;
// ─── Shape helpers ────────────────────────────────────────────────────────────
/** Normalize a cell set so its minimum row and column are 0. */
function normalize(cells) {
    const minR = Math.min(...cells.map((c) => c.r));
    const minC = Math.min(...cells.map((c) => c.c));
    return cells.map((c) => ({ r: c.r - minR, c: c.c - minC }));
}
/** Inclusive integer range `[from..to]`; empty when `to < from`. */
function range(from, to) {
    const out = [];
    for (let i = from; i <= to; i++)
        out.push(i);
    return out;
}
/** Deterministic Fisher–Yates on a copy — the caller keeps its input. */
function shuffled(items, rng) {
    const out = items.slice();
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}
/** Rotate a cell set by 90° clockwise `turns` times. */
export function rotateCells(cells, turns) {
    let out = cells;
    for (let t = 0; t < ((turns % 4) + 4) % 4; t++) {
        out = normalize(out.map((c) => ({ r: c.c, c: -c.r })));
    }
    return normalize(out);
}
/**
 * Place exactly the recipe's shapes into free cells; null if the board is too
 * tight. Biggest first — a 2×3 that cannot find room after five 1×2s have been
 * scattered around it wastes the whole attempt, and the denser boards this WP
 * asks for make that failure common. Position and rotation stay random, so the
 * ordering costs no variety.
 */
function placeShapes(shapeIds, gridSize, rng) {
    const order = shapeIds
        .map((id, index) => ({ id, index }))
        .sort((a, b) => {
        const size = (SHAPE_TEMPLATES[b.id]?.length ?? 0) - (SHAPE_TEMPLATES[a.id]?.length ?? 0);
        return size !== 0 ? size : a.index - b.index;
    });
    const occupied = new Set();
    const placed = [];
    for (let i = 0; i < order.length; i++) {
        const shapeId = order[i].id;
        const template = SHAPE_TEMPLATES[shapeId];
        if (!template)
            return null;
        let landed = false;
        for (let tries = 0; tries < PLACEMENT_TRIES && !landed; tries++) {
            const cells = rotateCells(template, Math.floor(rng() * 4));
            const height = Math.max(...cells.map((c) => c.r)) + 1;
            const width = Math.max(...cells.map((c) => c.c)) + 1;
            if (height > gridSize || width > gridSize)
                continue;
            const row = Math.floor(rng() * (gridSize - height + 1));
            const col = Math.floor(rng() * (gridSize - width + 1));
            const keys = cells.map((c) => `${row + c.r},${col + c.c}`);
            if (keys.some((k) => occupied.has(k)))
                continue;
            keys.forEach((k) => occupied.add(k));
            placed.push({ id: `b${i}`, shapeId, cells, row, col });
            landed = true;
        }
        if (!landed)
            return null;
    }
    return placed;
}
// ─── Gates ────────────────────────────────────────────────────────────────────
const WALLS = ['up', 'down', 'left', 'right'];
/**
 * The gate opening a block needs on a given wall: exiting up/down presents the
 * block's WIDTH to the wall, exiting left/right presents its HEIGHT.
 */
function spanFor(block, wall) {
    const bb = boundingBox(block);
    return wall === 'up' || wall === 'down' ? bb.width : bb.height;
}
/**
 * Gates + arrow locks, resolved together (WP-35.8 T2).
 *
 * These CANNOT be chosen independently: an axis-locked block can only ever leave
 * through a gate on the axis it can still travel, in a lane it already occupies.
 * A horizontally-locked block with a gate on the top wall — or on the right wall
 * but two rows away — is permanently stuck, which makes the whole board
 * unsolvable. (Measured: with independent choices, ~85% of Hard/Expert candidates
 * were rejected as unsolvable, each burning the solver's full node budget.)
 *
 * So: locks are proposed first, then each colour's gate is placed on a wall and
 * lane that keeps its locked block able to exit. A lock that cannot be honoured —
 * two locked blocks of one colour wanting different walls or disjoint lanes — is
 * dropped back to `free` rather than shipping a dead board.
 *
 * Gates also **never share a wall lane** (fixed 2026-07-23, user-reported): the
 * first version chose each colour's wall and offset independently, which drew one
 * gate on top of another on 106 of the first 150 levels. Overlapping gates are not
 * a rendering glitch — a lane belonging to two colours means the board no longer
 * tells the player what it accepts. Walls and offsets are now tried in a
 * deterministic shuffled order and only free lanes are taken; a colour that cannot
 * be placed anywhere rejects the candidate board.
 */
function designGatesAndLocks(blocks, proposedLocks, gridSize, rng) {
    const byColour = new Map();
    for (const b of blocks) {
        const list = byColour.get(b.colorId);
        if (list)
            list.push(b);
        else
            byColour.set(b.colorId, [b]);
    }
    const exits = [];
    const locks = new Map();
    /** Wall lanes a gate already occupies — no two gates may share one. */
    const takenLanes = new Map(WALLS.map((w) => [w, new Set()]));
    for (const [colorId, group] of byColour) {
        // The first locked block of this colour dictates the wall axis; any other
        // lock in the group survives only if the same gate can serve it too.
        const anchor = group.find((b) => proposedLocks.has(b.id));
        const anchorLock = anchor ? proposedLocks.get(anchor.id) : undefined;
        const candidateWalls = shuffled(anchorLock === 'horizontalOnly'
            ? ['left', 'right']
            : anchorLock === 'verticalOnly'
                ? ['up', 'down']
                : [...WALLS], rng);
        const gateAt = (wall, offset, span) => ({
            position: wall === 'left' || wall === 'right'
                ? { row: offset, col: wall === 'left' ? 0 : gridSize - 1 }
                : { row: wall === 'up' ? 0 : gridSize - 1, col: offset },
            direction: wall,
            colorId,
            span,
        });
        // Try each allowed wall until one has room; a colour that fits nowhere means
        // this candidate board's gates cannot be built at all.
        let placed = null;
        /**
         * A placement that works but starts a block flush against its own gate. Kept
         * only as a last resort: taking one immediately (the WP-35.8 behaviour) threw
         * the WHOLE board away at the caller's `preExited` check, which was the single
         * biggest source of wasted attempts once L-shapes arrived — 30–61 rejects on
         * the slowest levels. Trying the next wall first costs nothing.
         */
        let preExiting = null;
        for (const wall of candidateWalls) {
            const horizontal = wall === 'left' || wall === 'right';
            // The gate must be wide enough for every block of this colour…
            const span = Math.max(...group.map((b) => spanFor(b, wall)));
            if (span > gridSize)
                continue;
            // …and, when it serves a locked block, aligned to the lane that block is
            // stuck in (it can never change lanes).
            let low = 0;
            let high = gridSize - span;
            if (anchor) {
                const bb = boundingBox(anchor);
                const laneStart = horizontal ? anchor.position.row : anchor.position.col;
                const laneEnd = laneStart + (horizontal ? bb.height : bb.width) - 1;
                low = Math.max(0, laneEnd - span + 1);
                high = Math.min(gridSize - span, laneStart);
                if (low > high)
                    continue;
            }
            // Only offsets whose every lane is still free, in a deterministic shuffled
            // order; then prefer one that leaves NO block of this colour already flush
            // against the gate (such a board would clear itself on load).
            const taken = takenLanes.get(wall);
            const offsets = shuffled(range(low, high), rng).filter((o) => range(o, o + span - 1).every((lane) => !taken.has(lane)));
            if (offsets.length === 0)
                continue;
            const clean = offsets.find((o) => 
            // Module 53 — against the REAL board. The clearance clause needs the
            // other blocks' cells, and an empty set here would let a candidate gate
            // read as self-clearing when a neighbour actually blocks the sweep,
            // rejecting a board that is perfectly good.
            !group.some((b) => exitsThroughGate(b, gateAt(wall, o, span), gridSize, occupiedKeysExcept(blocks, b.id))));
            if (clean === undefined) {
                preExiting ?? (preExiting = { wall, offset: offsets[0], span });
                continue;
            }
            placed = { wall, offset: clean, span };
            break;
        }
        placed ?? (placed = preExiting);
        if (!placed)
            return null;
        const { wall, offset, span } = placed;
        const horizontal = wall === 'left' || wall === 'right';
        if (anchor)
            locks.set(anchor.id, anchorLock);
        range(offset, offset + span - 1).forEach((lane) => takenLanes.get(wall)?.add(lane));
        // Other locked blocks of this colour keep their lock only if this same gate
        // still lets them out.
        for (const b of group) {
            if (b === anchor)
                continue;
            const lock = proposedLocks.get(b.id);
            if (!lock)
                continue;
            const axisFits = (lock === 'horizontalOnly' && horizontal) || (lock === 'verticalOnly' && !horizontal);
            if (!axisFits)
                continue;
            const bb = boundingBox(b);
            const laneStart = horizontal ? b.position.row : b.position.col;
            const laneEnd = laneStart + (horizontal ? bb.height : bb.width) - 1;
            if (laneStart >= offset && laneEnd <= offset + span - 1)
                locks.set(b.id, lock);
        }
        exits.push(gateAt(wall, offset, span));
    }
    return {
        exits,
        blocks: blocks.map((b) => ({ ...b, movementConstraint: locks.get(b.id) ?? 'free' })),
    };
}
// ─── Board quality (WP-35.11) ─────────────────────────────────────────────────
const DIRECTIONS = ['up', 'down', 'left', 'right'];
/**
 * How many blocks CANNOT leave on their own — i.e. sliding them as far as they go
 * in any legal direction does not put them at their gate, so some other block has
 * to move first. This is the board's dependency count, and it is what separates a
 * puzzle from a set of blocks that each drive straight out.
 *
 * Cheap (one slide per block per direction, no search), so it runs BEFORE the
 * solver and cuts the expensive call on boards that were never interesting.
 */
export function dependencyCount(blocks, exits, gridSize, plates) {
    let dependent = 0;
    // Module 63 — from the START position: this asks whether a block can leave
    // on its own from where the board opens, so the power set is the opening one.
    const opts = { poweredPlateIds: poweredPlateIds(blocks, plates) };
    for (const block of blocks) {
        const others = occupiedKeysExcept(blocks, block.id);
        const free = DIRECTIONS.some((dir) => {
            // WP-50.4 — the slide stops at a gate the block can go through, so this
            // asks the same question the board answers. `null` means it cannot move
            // that way at all, and a block that is ALREADY sitting at its gate still
            // counts as free, which is what the old `?? block` fallback was for.
            const outcome = slideAndExitAgainst(block, dir, others, exits, gridSize, opts);
            // `others` is this block's own exclusion set, built above — exactly what
            // the clearance clause needs.
            if (!outcome)
                return exitingGate(block, exits, gridSize, others, opts) !== null;
            return outcome.exited !== null;
        });
        if (!free)
            dependent++;
    }
    return dependent;
}
/**
 * A key for "this is the same board again" (WP-35.11): shapes, where they sit,
 * and the gates. Deliberately colour- and arrow-agnostic — a board whose colours
 * were reshuffled is the same puzzle to play, so it should still count as a
 * repeat of the level before it.
 */
const TABLE_DIRECTIONS = {
    U: 'up',
    D: 'down',
    L: 'left',
    R: 'right',
};
/**
 * WP-83.5b — FNV-1a over the layout signature, as 8 hex digits: what the
 * solution table stores in place of the signature itself (a signature runs to
 * ~100 characters; 120 of them were a measurable share of the main chunk).
 */
export function signatureHash(signature) {
    let h = 0x811c9dc5;
    for (let i = 0; i < signature.length; i += 1) {
        h ^= signature.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(16).padStart(8, '0');
}
/**
 * WP-83.5b — the shipped route for a ramp board, or null when the table has no
 * entry for this level + variant or its signature hash does not match the board
 * generated now (a config change moved it: search instead, and let
 * `solution-table.test.ts` say the table is stale).
 */
export function tableRoute(levelNumber, variant, signature) {
    const entry = SOLUTION_TABLE[`${levelNumber}:${variant}`];
    if (!entry || entry[0] !== signatureHash(signature))
        return null;
    return entry[1].split(' ').map((token) => ({
        blockId: token.slice(0, -1),
        direction: TABLE_DIRECTIONS[token.slice(-1)],
    }));
}
export function layoutSignature(blocks, exits, plates) {
    const cells = blocks
        .map((b) => occupiedCells(b)
        .map((c) => `${c.row}.${c.col}`)
        .sort()
        .join('|'))
        .sort()
        .join('/');
    const gates = exits
        .map((e) => `${e.direction}:${e.position.row}.${e.position.col}:${e.span ?? 1}` +
        // Module 63 — a powered gate is a different puzzle from the same gate
        // open; a board with no plates keeps the pre-63 signature exactly.
        (e.powered ? '*' : ''))
        .sort()
        .join('/');
    const plateKey = plates && plates.length > 0
        ? '#' +
            plates
                .map((p) => `${p.cell.row}.${p.cell.col}`)
                .sort()
                .join('/')
        : '';
    return `${cells}#${gates}${plateKey}`;
}
// ─── Powered gates (Module 63, WP-63.D2/D3) ───────────────────────────────────
/**
 * How many gates should be powered at this level (WP-63.D1).
 *
 * Exported and pure for the same reason `arrowCountFor` is: the curve is a
 * design artifact and must be assertable without building a board. Zero before
 * the unlock; on the unlock level itself at least one (§3 rule 1 — the popup
 * names something the player can see); otherwise the band ramp, rounded. Capped
 * so `keepUnpoweredGates` gates stay open — the decision-7 accept-rate heuristic,
 * not a solvability rule — and never more than the board has gates.
 */
export function poweredCountFor(config, levelNumber, gateCount) {
    if (levelNumber < config.unlocks.poweredGatesUnlockLevel)
        return 0;
    const ramp = frameworkBandRamp(config, levelNumber, config.powered.countPerBand);
    const introducing = levelNumber === config.unlocks.poweredGatesUnlockLevel ? 1 : 0;
    const cap = Math.max(0, gateCount - config.powered.keepUnpoweredGates);
    return Math.min(cap, Math.max(introducing, Math.round(ramp)));
}
/**
 * Wire `count` gates to plates (WP-63.D2). Returns the exits with `powered`
 * set on the chosen ones and the plates they name; `unroutable` counts pairs
 * the trace geometry refused (WP-63.D3), which with the current routing rule
 * is only ever an out-of-grid cell and is kept as a reject reason so it can
 * never become silent.
 *
 * **Consumes no randomness when `count` is 0** — that is what keeps every
 * level before the unlock byte-identical to its pre-Module-63 board: the rng
 * stream the placement and gates were drawn from is not disturbed.
 *
 * Plate cells are chosen within `cap` cells of the gate's wall, preferring
 * cells no block starts on (a board that opens with a gate already lit has
 * taught nothing), never on another plate, in a deterministic shuffled order.
 * Colour uniqueness needs no enforcing here: `designGatesAndLocks` builds one
 * gate per colour, so every gate's colour is already unique on its board.
 *
 * Module 75 — never on a cell the gate's own block covers at an exit position
 * through that gate (`selfOpeningCells`), or the block would open its own gate
 * and the shutter would mean nothing. If every cell is refused the gate stays
 * unpowered this attempt, as an all-unroutable gate does.
 */
function designPlates(blocks, exits, gridSize, count, cap, rng) {
    if (count <= 0 || exits.length === 0)
        return { exits, plates: [], unroutable: 0, selfOpening: 0 };
    const occupied = occupiedKeys(blocks);
    const taken = new Set();
    const plates = [];
    const poweredBy = new Map();
    let unroutable = 0;
    let selfOpening = 0;
    for (const exit of shuffled(exits, rng)) {
        if (plates.length >= count)
            break;
        const cells = [];
        for (let row = 0; row < gridSize; row++)
            for (let col = 0; col < gridSize; col++) {
                const cell = { row, col };
                if (cap !== null && traceDistance(cell, exit, gridSize) > cap)
                    continue;
                if (taken.has(`${row},${col}`))
                    continue;
                cells.push(cell);
            }
        const free = cells.filter((c) => !occupied.has(`${c.row},${c.col}`));
        const ordered = [
            ...shuffled(free, rng),
            ...shuffled(cells.filter((c) => !free.includes(c)), rng),
        ];
        // Module 75 (D1/D2) — a plate the gate's own block covers while leaving
        // opens its own gate. Skipped HERE, after both shuffles, never filtered out
        // before them: `shuffled` draws one number per element, so a shorter list
        // would move the rng stream for every powered board, not just these.
        const selfOpeningAt = selfOpeningCells(exit, blocks, gridSize);
        const cell = ordered.find((c) => {
            if (selfOpeningAt.has(`${c.row},${c.col}`)) {
                selfOpening++;
                return false;
            }
            const ok = traceRoutes(c, exit, gridSize);
            if (!ok)
                unroutable++;
            return ok;
        });
        if (!cell)
            continue;
        const id = `p${plates.length}`;
        plates.push({ id, cell, gateColorId: exit.colorId ?? '' });
        poweredBy.set(exit, id);
        taken.add(`${cell.row},${cell.col}`);
    }
    return {
        exits: exits.map((e) => {
            const plateId = poweredBy.get(e);
            return plateId ? { ...e, powered: { plateId } } : e;
        }),
        plates,
        unroutable,
        selfOpening,
    };
}
// ─── Generation ───────────────────────────────────────────────────────────────
/**
 * Propose axis locks for this level (none before the Arrows unlock). These are
 * proposals only — `designGatesAndLocks` keeps the ones it can still build a
 * reachable gate for and drops the rest, so a lock can never strand a block.
 */
function proposeLocks(blocks, config, levelNumber, rng) {
    const proposed = new Map();
    if (levelNumber < config.unlocks.arrowsUnlockLevel)
        return proposed;
    return proposeLocksFor(blocks, arrowCountFor(config, levelNumber, blocks.length), rng);
}
/**
 * How many blocks should carry an axis lock at this level (WP-36.6).
 *
 * Exported and pure so the arrow curve can be measured and asserted without
 * building a board — the shipped curve is a design artifact, and before this it
 * could only be observed by generating levels and counting.
 *
 * Three changes from the WP-35.14 rule, all of which exist because the count sat
 * at exactly 1 from level 5 to level 70:
 *  - the fraction RAMPS within its framework band rather than being flat across
 *    a gameplay band;
 *  - `Math.round`, not `Math.floor` — flooring meant a level had to reach a full
 *    extra arrow's worth of blocks before the count moved at all, so 5 blocks at
 *    0.3 (= 1.5 arrows) shipped one;
 *  - a per-band `minArrowsPerBand` floor, so a recovery level's smaller board
 *    cannot silently take the mechanic away again.
 */
export function arrowCountFor(config, levelNumber, blockCount) {
    if (levelNumber < config.unlocks.arrowsUnlockLevel)
        return 0;
    const fraction = frameworkBandRamp(config, levelNumber, config.arrows.constraintFractionPerBand);
    const band = frameworkBandForLevel(config, levelNumber);
    // On the Arrows unlock level itself, at least one block carries a lock whatever
    // the fraction rounds to — the mechanic is being introduced, so it has to be on
    // the board (WP-35.14; the same guarantee the shape families get).
    const introducing = levelNumber === config.unlocks.arrowsUnlockLevel ? 1 : 0;
    const floor = Math.max(introducing, config.arrows.minArrowsPerBand[band.name]);
    // Never more locks than blocks — a fully locked board is a fraction ≥ 1, which
    // the schema allows and which `designGatesAndLocks` would mostly reject anyway.
    return Math.min(blockCount, Math.max(floor, Math.round(blockCount * fraction)));
}
/** Place `lockCount` locks on distinct lanes. Split out so it is testable alone. */
function proposeLocksFor(blocks, lockCount, rng) {
    const proposed = new Map();
    if (lockCount <= 0)
        return proposed;
    // Deterministic shuffle of indexes, then lock the first `lockCount`.
    const order = shuffled(blocks.map((_, i) => i), rng);
    // A locked block can never leave its lane, so two locks in the SAME lane can
    // deadlock each other permanently (the rear one can never pass the front one to
    // reach a shared wall). Those boards are unsolvable by construction and were
    // the bulk of the solver's expensive rejections, so locks are kept to distinct
    // lanes per axis instead.
    const usedRows = new Set();
    const usedCols = new Set();
    for (const i of order) {
        if (proposed.size >= lockCount)
            break;
        const block = blocks[i];
        const bb = boundingBox(block);
        const rows = Array.from({ length: bb.height }, (_, r) => block.position.row + r);
        const cols = Array.from({ length: bb.width }, (_, c) => block.position.col + c);
        const horizontalFree = rows.every((r) => !usedRows.has(r));
        const verticalFree = cols.every((c) => !usedCols.has(c));
        if (!horizontalFree && !verticalFree)
            continue;
        const horizontal = horizontalFree && (!verticalFree || rng() < 0.5);
        proposed.set(block.id, horizontal ? 'horizontalOnly' : 'verticalOnly');
        if (horizontal)
            rows.forEach((r) => usedRows.add(r));
        else
            cols.forEach((c) => usedCols.add(c));
    }
    return proposed;
}
function toState(blocks, exits, gridSize, difficulty, levelNumber, plates = []) {
    return {
        // `plates` only when there are any, so a pre-unlock level's state object is
        // exactly the one it was before Module 63.
        puzzle: { levelNumber, gridSize, exits, difficulty, ...(plates.length ? { plates } : {}) },
        blocks,
        moveCount: 0,
        hintsUsed: 0,
        undoStack: [],
    };
}
/**
 * The last-resort board: one 1×2 block sitting one step away from its own gate.
 * Only used if every attempt failed (density too high for the grid, say) — it is
 * always solvable, so the game can never be handed an unplayable level.
 */
function fallbackBoard(config, gridSize, difficulty, levelNumber) {
    const colorId = config.colours.palette[0];
    const block = {
        id: 'b0',
        colorId,
        size: 2,
        orientation: 'horizontal',
        type: 'target',
        position: { row: 1, col: 1 },
        cells: SHAPE_TEMPLATES['1x2'],
        movementConstraint: 'free',
    };
    const exit = {
        position: { row: 1, col: gridSize - 1 },
        direction: 'right',
        colorId,
        span: 1,
    };
    return toState([block], [exit], gridSize, difficulty, levelNumber);
}
/**
 * Generate the free-slide board for `levelNumber`. Same level number → same
 * board, always. Every returned board has been solver-verified.
 */
export function generateFreeLevel(levelNumber, options = {}) {
    const config = options.config ?? DEFAULT_GENERATION_CONFIG;
    // The framework curve owns level→band (WP-35.10), and since WP-50.23 it is the
    // ONLY thing that does: the rival `bands[].levelRange` lookup was deleted with
    // the helper that read it. `bandConfigFor` then says how a board of that band
    // is built.
    const levelTarget = targetScoreForLevel(config, levelNumber);
    const band = options.difficulty ?? levelTarget.difficulty;
    const cfg = bandConfigFor(config, band);
    const variant = options.seed ?? 0;
    const rng = mulberry32(levelNumber * SEED_SALT + SEED_OFFSET + variant * SEED_VARIANT_STRIDE);
    // WP-35.11: shapes, colours and block count are decided ONCE, up front, judged
    // against the recipes of the levels before this one — so consecutive levels
    // differ in what they are made of, not only in where the pieces landed.
    const recipe = recipeForLevel(config, levelNumber, options.difficulty, variant, options.recipeChain);
    const avoidLayouts = new Set(options.avoidLayouts ?? []);
    let attempts = 0;
    const rejects = {
        placement: 0,
        gates: 0,
        preExited: 0,
        tooLoose: 0,
        repeatedLayout: 0,
        missingArrows: 0,
        unsolvable: 0,
        tooEasy: 0,
        missingPowered: 0,
        unroutableTrace: 0,
        selfOpeningGate: 0,
    };
    let best = null;
    let scored = 0;
    /** Build the returned level from the chosen candidate. */
    const finish = (chosen) => {
        // The EXACT score is paid at most once, on the chosen board only, and only
        // when asked for (see `FreeGenerateOptions.score`).
        const difficulty = options.score
            ? scoreDifficulty(measureBoard(chosen.blocks, chosen.exits, cfg.gridSize, { plates: chosen.plates }), config.difficultyScoreWeights)
            : null;
        // Module 83 — paid once, on the chosen board only. Searched from the
        // starting layout with the plates and nothing else, exactly as a fresh
        // attempt begins.
        const signature = layoutSignature(chosen.blocks, chosen.exits, chosen.plates);
        // WP-83.5b — the ramp puzzles' routes ship in a table keyed by level and
        // variant, guarded by the board's signature, so no player waits for a
        // search on a board the repo already knows. Anything else is searched.
        const tabled = options.solution === false ? null : tableRoute(levelNumber, variant, signature);
        const solution = options.solution === false
            ? null
            : (tabled ??
                (options.solution === 'table-only'
                    ? null
                    : shortSolve(chosen.blocks, chosen.exits, cfg.gridSize, {
                        maxNodes: config.moveBudget.solution.maxNodes,
                        plates: chosen.plates,
                    }).moves));
        return {
            state: toState(chosen.blocks, chosen.exits, cfg.gridSize, band, levelNumber, chosen.plates),
            metrics: {
                attempts,
                clearingDepth: chosen.clearingDepth,
                solution,
                solutionSource: solution === null ? null : tabled ? 'table' : 'search',
                difficultyScore: difficulty?.score ?? null,
                difficultyMetrics: difficulty?.metrics ?? null,
                targetScore: levelTarget.target,
                proxyScore: chosen.proxyScore,
                proxyPar: chosen.proxyPar,
                frameworkBand: levelTarget.band.name,
                isRecovery: levelTarget.isRecovery,
                isMilestone: levelTarget.isMilestone,
                nodes: chosen.nodes,
                blockCount: chosen.blocks.length,
                colourCount: new Set(chosen.blocks.map((b) => b.colorId)).size,
                shapeIds: chosen.shapeIds,
                arrowCount: chosen.blocks.filter((b) => (b.movementConstraint ?? 'free') !== 'free').length,
                poweredCount: chosen.plates.length,
                dependencies: chosen.dependencies,
                layoutSignature: signature,
                usedFallback: false,
                rejects,
            },
        };
    };
    // Ease the block count down if the board is too tight to place or solve —
    // dropping shapes off the end of the recipe, so a level that has to shrink is
    // still built from the same draw rather than a different one.
    for (let count = recipe.blockCount; count >= 1; count--) {
        for (let attempt = 0; attempt < cfg.maxAttempts; attempt++) {
            // The attempt budget has to be checked HERE, not only where a candidate is
            // scored: on a crowded board the expensive attempts are the REJECTED ones,
            // and a run that keeps failing would otherwise never reach the check.
            // Only ever applied once a usable board exists.
            if (best && attempts >= config.curve.candidateAttemptBudget)
                return finish(best);
            attempts++;
            const placed = placeShapes(recipe.shapeIds.slice(0, count), cfg.gridSize, rng);
            if (!placed) {
                rejects.placement++;
                continue;
            }
            const candidates = placed.map((p, i) => ({
                id: p.id,
                colorId: recipe.colours[i % recipe.colours.length],
                size: p.cells.length,
                orientation: 'horizontal',
                type: 'target',
                position: { row: p.row, col: p.col },
                cells: p.cells,
                movementConstraint: 'free',
            }));
            const proposed = proposeLocks(candidates, config, levelNumber, rng);
            const design = designGatesAndLocks(candidates, proposed, cfg.gridSize, rng);
            if (!design) {
                rejects.gates++;
                continue;
            }
            const { blocks } = design;
            // Module 63 (WP-63.D2/D3) — wire some gates to plates. Draws nothing from
            // the rng before the unlock, so earlier levels are the boards they were.
            const poweredTarget = options.poweredCount === undefined
                ? poweredCountFor(config, levelNumber, design.exits.length)
                : Math.min(Math.max(0, design.exits.length - config.powered.keepUnpoweredGates), options.poweredCount);
            const wired = designPlates(blocks, design.exits, cfg.gridSize, poweredTarget, config.powered.traceLengthCapPerBand[band], rng);
            const { exits, plates } = wired;
            rejects.unroutableTrace += wired.unroutable;
            rejects.selfOpeningGate += wired.selfOpening;
            const opening = { poweredPlateIds: poweredPlateIds(blocks, plates) };
            // No block may start flush against a matching gate — it would exit instantly.
            if (findExitedFree(blocks, exits, cfg.gridSize, opening).length > 0) {
                rejects.preExited++;
                continue;
            }
            // Both remaining screens are CHEAP and run before the solver, so a board
            // that fails them never costs a search.
            // (1) Density is only half the story — a crowded board where every block
            // still drives straight out is not a puzzle (WP-35.11 T1).
            const dependencies = dependencyCount(blocks, exits, cfg.gridSize, plates);
            if (dependencies < config.density.startDependencies) {
                rejects.tooLoose++;
                continue;
            }
            // (2) Don't rebuild a layout the player just played (WP-35.11 T2).
            if (avoidLayouts.size > 0 && avoidLayouts.has(layoutSignature(blocks, exits, plates))) {
                rejects.repeatedLayout++;
                continue;
            }
            // (2b) The powered-gate unlock level must actually SHOW one (Module 63,
            // the same promise the Arrows unlock keeps below). Module 73 — when the
            // caller states the count (`options.poweredCount`), THAT is the promise:
            // a plain endless puzzle can sit on the unlock level (Beginner 58 does)
            // and must not be rejected into the fallback for having no plate, and an
            // "on" puzzle must actually get the plate it was promised.
            const poweredShort = options.poweredCount === undefined
                ? levelNumber === config.unlocks.poweredGatesUnlockLevel && plates.length === 0
                : plates.length < poweredTarget;
            if (poweredShort) {
                rejects.missingPowered++;
                continue;
            }
            // (3) The Arrows unlock level must actually SHOW an arrow (WP-35.14). A
            // lock is only proposed — `designGatesAndLocks` drops any it cannot give a
            // reachable gate — so the guarantee has to be checked on the built board,
            // not on the proposal.
            if (levelNumber === config.unlocks.arrowsUnlockLevel &&
                !blocks.some((b) => (b.movementConstraint ?? 'free') !== 'free')) {
                rejects.missingArrows++;
                continue;
            }
            // Accept/reject uses the DFS clearability check, NOT BFS-shortest: the
            // optimal move count costs 30k–140k nodes on a 7×7/8×8 board, which would
            // be paid again on every rejected candidate. See `clearableFree`.
            // Module 63 — the solver knows the plates, so a board whose only way out
            // needs an anchor that can never arrive rejects itself here (decision 7).
            const check = clearableFree(blocks, exits, cfg.gridSize, {
                maxNodes: cfg.freeSolverLimit,
                plates,
            });
            if (!check.clearable) {
                rejects.unsolvable++;
                continue;
            }
            // "Too easy" still means the band's `minSolveMoves`, but proving it does
            // NOT need the optimal answer: a DEPTH-LIMITED BFS only has to show that no
            // clearing exists in fewer moves, which is cheap because BFS explores by
            // depth. If it hits its node budget without finding one, the board is
            // certainly not trivial, so it passes.
            if (cfg.minSolveMoves > 1) {
                const shortcut = solveFree(blocks, exits, cfg.gridSize, {
                    maxNodes: TOO_EASY_NODE_BUDGET,
                    maxDepth: cfg.minSolveMoves - 1,
                    plates,
                });
                if (shortcut.moves) {
                    rejects.tooEasy++;
                    continue;
                }
            }
            // The board is valid. Now aim it at the curve: score it with the PROXY
            // (a small-budget solve — the exact score costs more than the whole
            // generation budget) and keep the closest candidate to this level's
            // target, stopping early once one lands inside tolerance.
            const proxy = proxyScoreBoard(blocks, exits, cfg.gridSize, config.difficultyScoreWeights, plates);
            const distance = Math.abs(proxy.score - levelTarget.target);
            scored++;
            if (!best || distance < best.distance) {
                best = {
                    blocks,
                    exits,
                    plates,
                    shapeIds: placed.map((p) => p.shapeId),
                    clearingDepth: check.depth,
                    nodes: check.nodes,
                    proxyScore: proxy.score,
                    proxyPar: proxy.metrics.moves,
                    distance,
                    dependencies,
                };
            }
            // Stop early only on a COMFORTABLE hit, not a borderline one. The proxy
            // under-reads on boards its small budget cannot solve (measured: up to 8
            // points), so accepting the first candidate merely inside tolerance let
            // that bias through unchallenged; requiring half tolerance spends a few
            // more candidates and lands levels closer to the curve.
            //
            // `candidateAttemptBudget` is the safety valve on that spending: crowded
            // late boards reject most attempts as unsolvable, so chasing the quota can
            // cost hundreds of milliseconds. Once the budget is spent, the best board
            // found so far is the answer.
            const budgetSpent = attempts >= config.curve.candidateAttemptBudget;
            if (distance > levelTarget.tolerance / 2 &&
                scored < config.curve.candidates &&
                !budgetSpent) {
                continue;
            }
            return finish(best);
        }
    }
    if (best)
        return finish(best);
    const state = fallbackBoard(config, cfg.gridSize, band, levelNumber);
    const fallbackDifficulty = options.score
        ? scoreDifficulty(measureBoard(state.blocks, state.puzzle.exits, cfg.gridSize), config.difficultyScoreWeights)
        : null;
    return {
        state,
        metrics: {
            attempts,
            clearingDepth: 1,
            // One block, one slide: the search is instant, and the Reveal can show it.
            // One block, one slide: instant, so 'table-only' searches it too.
            solution: options.solution === false
                ? null
                : shortSolve(state.blocks, state.puzzle.exits, cfg.gridSize, {
                    maxNodes: config.moveBudget.solution.maxNodes,
                }).moves,
            solutionSource: options.solution === false ? null : 'search',
            difficultyScore: fallbackDifficulty?.score ?? null,
            difficultyMetrics: fallbackDifficulty?.metrics ?? null,
            targetScore: levelTarget.target,
            proxyScore: null,
            proxyPar: null,
            frameworkBand: levelTarget.band.name,
            isRecovery: levelTarget.isRecovery,
            isMilestone: levelTarget.isMilestone,
            nodes: 0,
            blockCount: 1,
            colourCount: 1,
            shapeIds: ['1x2'],
            arrowCount: 0,
            poweredCount: 0,
            dependencies: 0,
            layoutSignature: layoutSignature(state.blocks, state.puzzle.exits),
            usedFallback: true,
            rejects,
        },
    };
}
/** Cell keys a board occupies — exported for the Studio's overlap diagnostics. */
export function occupiedKeys(blocks) {
    return new Set(blocks.flatMap((b) => occupiedCells(b).map((c) => `${c.row},${c.col}`)));
}
/**
 * A generator that remembers the levels it just produced (WP-35.11).
 *
 * `generateFreeLevel` is deliberately a pure function of (config, level): it can
 * be called for any level, in any order, and always returns the same board. That
 * leaves nowhere to keep "what did the last five levels look like", which the
 * no-repeat-layout window needs — so the memory lives here instead, in an object
 * the caller owns and injects (no module-level state, per the module's DI rule).
 *
 * Shape and colour anti-repetition do NOT depend on this: those are derived from
 * the recipe chain and hold even for a one-off `generateFreeLevel` call.
 */
export class FreeLevelGenerator {
    constructor(config = DEFAULT_GENERATION_CONFIG) {
        this.config = config;
        /** level → layout signature, oldest first (Map preserves insertion order). */
        this.seen = new Map();
        this.recipeChain = new RecipeChain(config);
    }
    /** The window of levels a new board must not reproduce. */
    get window() {
        return this.config.antiRepetition.noRepeatWindow;
    }
    generate(levelNumber, options = {}) {
        const avoid = [];
        for (let level = levelNumber - this.window; level < levelNumber; level++) {
            const signature = this.seen.get(level);
            if (signature)
                avoid.push(signature);
        }
        const level = generateFreeLevel(levelNumber, {
            config: this.config,
            recipeChain: this.recipeChain,
            ...options,
            avoidLayouts: avoid,
        });
        this.seen.delete(levelNumber);
        this.seen.set(levelNumber, level.metrics.layoutSignature);
        // Bound the memory: a player who walks 1..500 should not accumulate 500 keys.
        while (this.seen.size > Math.max(1, this.window) * 4) {
            this.seen.delete(this.seen.keys().next().value);
        }
        return level;
    }
    /** Forget the history (e.g. the Studio switching config). */
    reset() {
        this.seen.clear();
        this.recipeChain.reset();
    }
}
